import React, { useState, useEffect, useRef, useMemo, useLayoutEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DateCalendar = ({
  isOpen,
  position,
  onClose,
  onSave,
  onRefresh, // NEW PROP
  initialDateRange,
  menuRef,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date(2025, 8, 1));
  const [selectedRange, setSelectedRange] = useState({
    start: null,
    end: null,
  });

  const [initialSelectedDate, setInitialSelectedDate] = useState(null);
  const [saveState, setSaveState] = useState("idle"); // 'idle' | 'saving' | 'saved'
  const closeTimerRef = useRef(null);

  // --- Dynamic Position State to Prevent Trimming ---
  const [adjustedPosition, setAdjustedPosition] = useState({ top: -9999, left: -9999 });

  // Adjust popup position if it hits the bottom of the screen
  useLayoutEffect(() => {
    if (isOpen && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      let newTop = position.top;
      
      // If the calendar extends below the viewport, render it above the cell instead
      if (rect.bottom > viewportHeight) {
         // position.top is `cellBottom + 5`, we subtract cellHeight + calendarHeight + gap
         const approximateCellHeight = 35; 
         newTop = position.top - rect.height - approximateCellHeight - 10;
      }
      
      setAdjustedPosition({ top: newTop, left: position.left });
    }
  }, [isOpen, position, menuRef]);

    // Sync state when calendar opens
    useEffect(() => {
      // Reset close timer and save state
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setSaveState("idle");
  
      if (isOpen) {
        if (initialDateRange?.start) {
          const initialStart = initialDateRange.start;
          setCurrentDate(new Date(initialStart.getFullYear(), initialStart.getMonth(), 1));
          setSelectedRange({ start: initialStart, end: initialStart });
          setInitialSelectedDate(initialStart);
        } else {
          // FALLBACK: Use TODAY's date if no initial range is found
          const today = new Date();
          // Strip the time component so it strictly represents midnight today
          const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          
          setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
          setSelectedRange({ start: todayMidnight, end: todayMidnight });
          setInitialSelectedDate(null); // Keep null so the user is allowed to "Save" today's date
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]); // <-- Removed initialDateRange from dependencies to stop the reset bug

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const selectedYear = currentDate.getFullYear();
    
    // Ensure range covers 2000 to (current + 10), AND includes the selected year
    const minYear = Math.min(2000, selectedYear - 5);
    const maxYear = Math.max(currentYear + 10, selectedYear + 5);
    
    return Array.from(
      { length: maxYear - minYear + 1 }, 
      (_, i) => minYear + i
    );
  }, [currentDate]); 

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);

  const handleDayClick = (day) => {
    const clickedDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );

    // Single date selection (for date editing use case)
    setSelectedRange({ start: clickedDate, end: clickedDate });
  };

  const isSelected = (day) => {
    if (!selectedRange.start) return false;
    const current = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );

    const isStart =
      current.toDateString() === selectedRange.start.toDateString();
    const isEnd =
      selectedRange.end &&
      current.toDateString() === selectedRange.end.toDateString();

    return isStart || isEnd;
  };

  const isInRange = (day) => {
    if (!selectedRange.start || !selectedRange.end) return false;
    const current = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    return current > selectedRange.start && current < selectedRange.end;
  };

  const hasUserChangedDate = (() => {
    if (!selectedRange.start) return false;
    if (!initialSelectedDate) return true;
    return (
      selectedRange.start.toDateString() !== initialSelectedDate.toDateString()
    );
  })();

  const isSaveDisabled =
    !hasUserChangedDate || saveState === "saving" || saveState === "saved";

    const handleSaveClick = async () => {
      if (isSaveDisabled) return;
  
      setSaveState("saving");
      try {
        const ok = await onSave(selectedRange);
        if (ok) {
          setSaveState("saved");
          
          // Wait 2 seconds (2000ms) before closing the modal
          closeTimerRef.current = setTimeout(() => {
            onClose();
            // Call refresh AFTER modal closes
            if (onRefresh) {
              onRefresh();
            }
          }, 1500); 
          
        } else {
          setSaveState("idle");
        }
      } catch (e) {
        setSaveState("idle");
      }
    };

  if (!isOpen) return null;

  const menuStyle = {
    top: adjustedPosition.top !== -9999 ? `${adjustedPosition.top}px` : `${position.top}px`,
    left: `${position.left}px`,
    visibility: adjustedPosition.top === -9999 ? "hidden" : "visible",
  };

  return (
    <div
      ref={menuRef}
      className="fixed z-[9999] bg-white rounded-xl shadow-xl border border-gray-100 py-2.5 px-2.5 w-[270px] font-outfit"
      style={menuStyle}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() =>
            setCurrentDate(
              new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
            )
          }
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>

        <div className="flex gap-2">
          <div className="relative">
            <select
              value={months[currentDate.getMonth()]}
              onChange={(e) =>
                setCurrentDate(
                  new Date(
                    currentDate.getFullYear(),
                    months.indexOf(e.target.value),
                    1
                  )
                )
              }
              className="appearance-none bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1 pr-7 text-xs font-medium text-gray-700 cursor-pointer focus:outline-none hover:bg-gray-100"
            >
              {months.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
              <svg className="h-3.5 w-3.5 fill-current" viewBox="0 0 20 20">
                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </div>
          </div>

          <div className="relative">
            <select
              value={currentDate.getFullYear()}
              onChange={(e) =>
                setCurrentDate(
                  new Date(parseInt(e.target.value), currentDate.getMonth(), 1)
                )
              }
              className="appearance-none bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1 pr-7 text-xs font-medium text-gray-700 cursor-pointer focus:outline-none hover:bg-gray-100"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
              <svg className="h-3.5 w-3.5 fill-current" viewBox="0 0 20 20">
                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </div>
          </div>
        </div>

        <button
          onClick={() =>
            setCurrentDate(
              new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
            )
          }
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* Days Header */}
      <div className="grid grid-cols-7 mb-1.5">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
          <div
            key={day}
            className="text-center text-[11px] font-medium text-gray-400 py-1"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-y-1 mb-3">
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const selected = isSelected(day);
          const inRange = isInRange(day);

          return (
            <button
              key={day}
              onClick={() => handleDayClick(day)}
              className={`
                h-8 w-8 mx-auto flex items-center justify-center text-xs rounded-lg transition-colors
                ${
                  selected
                    ? "bg-[#1F2937] text-white font-medium"
                    : inRange
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-700 hover:bg-gray-50"
                }
              `}
            >
              {day}
            </button>
          );
        })}
      </div>

      {/* Footer Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveClick}
          disabled={isSaveDisabled}
          className={`w-[40%] rounded-lg py-2 text-xs font-medium transition-colors ${
            isSaveDisabled
              ? "bg-gray-300 text-gray-600 cursor-not-allowed"
              : "bg-[#1F2937] text-white hover:bg-gray-800"
          }`}
        >
          {saveState === "saving"
            ? "Saving..."
            : saveState === "saved"
            ? "Saved"
            : "Save"}
        </button>
      </div>
    </div>
  );
};

export default DateCalendar;